import React, { useState, useEffect } from "react";
import { Form, Button, Spin, Row, Col, Input, TimePicker, Modal } from "antd";
import { ReactSVG } from "react-svg";
import { Link } from "react-router-dom";
import { isEmpty, union } from "lodash";
import { useSelector } from "react-redux";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";

import AddressInput from "../../Inputs/AddressInput";
import InfoLabel from "../../Elements/InfoLabel";
import { TIME_FORMAT_FRONTEND } from "../../../constants";
import "./BulkUpdate.scss";

import { getTranslation, setCountryCookie } from "../../../utils/helper";
import DefaultContactLocation from "./DefaultContactLocation";

import LabelComponent from "../../Elements/LabelComponent";
import TagInput from "../../Elements/TagInput";
import dayjs from "dayjs";

const BulkUpdateForm = ({ form, isFetching, handleSubmit, handleCancel, countryCodeInit }) => {
  const [labelIds, setLabelIds] = useState([]);
  const [types, setTypes] = useState([]);

  const user = useSelector(state => state.auth.user);
  const setting = useSelector(state => state.setting.setting) || user.company;

  const [countryPickupPhone, setCountryPickupPhone] = useState();
  const [countryDeliveryPhone, setCountryDeliveryPhone] = useState();
  const [state, setState] = useState({
    visibleModal: false,
    contactType: null,
    contact: null,
  });

  useEffect(() => {
    setLabelIds([]);
    setTypes([]);
  }, [isFetching]);

  const handleOnFinish = async values => {
    const params = setParams(values);
    handleSubmit(params);
  };

  const setParams = values => {
    const params = { ...values };
    params.pickupPhone =
      !isEmpty(values.pickupPhone) && countryPickupPhone?.dialCode !== values.pickupPhone
        ? `${values.pickupPhone.includes("+") ? "" : "+"}${values.pickupPhone}`
        : null;
    params.deliveryPhone =
      !isEmpty(values.deliveryPhone) && countryDeliveryPhone?.dialCode !== values.deliveryPhone
        ? `${values.deliveryPhone.includes("+") ? "" : "+"}${values.deliveryPhone}`
        : null;
    params.pickupAddress = values.pickupAddress?.address;
    params.pickupLat = values.pickupAddress?.latLng?.lat;
    params.pickupLng = values.pickupAddress?.latLng?.lng;
    params.timeStart = values.timeStart ? values.timeStart.format(TIME_FORMAT_FRONTEND) : null;
    params.timeEnd = values.timeEnd ? values.timeEnd.format(TIME_FORMAT_FRONTEND) : null;
    params.pickupDuration = values.pickupDuration ? Number(values.pickupDuration) : null;
    params.deliveryAddress = values.deliveryAddress?.address;
    params.deliveryLat = values.deliveryAddress?.latLng?.lat;
    params.deliveryLng = values.deliveryAddress?.latLng?.lng;
    params.deliveryTimeStart = values.deliveryTimeStart ? values.deliveryTimeStart.format(TIME_FORMAT_FRONTEND) : null;
    params.deliveryTimeEnd = values.deliveryTimeEnd ? values.deliveryTimeEnd.format(TIME_FORMAT_FRONTEND) : null;
    params.deliveryDuration = values.deliveryDuration ? Number(values.deliveryDuration) : null;
    params.requiredSkills = types;
    params.labels = labelIds;
    return params;
  };

  const handleOnChangePickupFrom = value => {
    form.setFieldsValue({ timeStart: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue("timeEnd"))) {
      resetErrorPickupTime();
      return;
    }
    if (form.getFieldValue("timeEnd") < value) {
      form.setFields([
        {
          name: "timeStart",
          errors: ["Shift End must be less than Shift Start"],
        },
      ]);
    }
  };

  const handleOnChangePickupTo = value => {
    form.setFieldsValue({ timeEnd: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue("timeStart"))) {
      resetErrorPickupTime();
      return;
    }
    if (form.getFieldValue("timeStart") > value) {
      form.setFields([
        {
          name: "timeEnd",
          errors: ["Pickup To must be greater than Pickup From"],
        },
      ]);
    }
  };

  const resetErrorPickupTime = () => {
    form.setFields([
      {
        name: "timeStart",
        errors: [],
      },
      {
        name: "timeEnd",
        errors: [],
      },
    ]);
  };

  const handleOnChangeDeliveryFrom = value => {
    form.setFieldsValue({ deliveryTimeStart: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue("deliveryTimeEnd"))) {
      resetErrorDeliveryTime();
      return;
    }
    if (form.getFieldValue("deliveryTimeEnd") < value) {
      form.setFields([
        {
          name: "deliveryTimeStart",
          errors: ["Delivery From must be less than Delivery To"],
        },
      ]);
    }
  };

  const handleOnChangeDeliveryTo = value => {
    form.setFieldsValue({ deliveryTimeEnd: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue("deliveryTimeStart"))) {
      resetErrorDeliveryTime();
      return;
    }
    if (form.getFieldValue("deliveryTimeStart") > value) {
      form.setFields([
        {
          name: "deliveryTimeEnd",
          errors: ["Delivery To must be greater than Delivery From"],
        },
      ]);
    }
  };

  const resetErrorDeliveryTime = () => {
    form.setFields([
      {
        name: "deliveryTimeStart",
        errors: [],
      },
      {
        name: "deliveryTimeEnd",
        errors: [],
      },
    ]);
  };

  const handleUseDefaultLocation = type => {
    setState({
      ...state,
      visibleModal: true,
      contactType: type,
      contact: null,
    });
  };

  const handleSubmitSelectContact = () => {
    if (state.contact) {
      if (state.contactType === "PICKUP") {
        form.setFieldsValue({
          pickupName: state.contact.name,
          pickupPhone: state.contact.phone,
          timeStart: state.contact.startTime ? dayjs(state.contact.startTime, TIME_FORMAT_FRONTEND) : null,
          timeEnd: state.contact.endTime ? dayjs(state.contact.endTime, TIME_FORMAT_FRONTEND) : null,
          pickupDuration: state.contact.duration,
          pickupAddress: {
            address: state.contact.address,
            latLng: {
              lat: state.contact.lat,
              lng: state.contact.lng,
            },
          },
          pickupNotes: state.contact.notes,
        });
      }
      if (state.contactType === "DELIVERY") {
        form.setFieldsValue({
          deliveryName: state.contact.name,
          deliveryPhone: state.contact.phone,
          deliveryTimeStart: state.contact.startTime ? dayjs(state.contact.startTime, TIME_FORMAT_FRONTEND) : null,
          deliveryTimeEnd: state.contact.endTime ? dayjs(state.contact.endTime, TIME_FORMAT_FRONTEND) : null,
          deliveryDuration: state.contact.duration,
          deliveryAddress: {
            address: state.contact.address,
            latLng: {
              lat: state.contact.lat,
              lng: state.contact.lng,
            },
          },
          deliveryNotes: state.contact.notes,
          load: state.contact.load,
        });
      }
      setTypes(union(types, state.contact.types));
    }
    handleCancelSelectContact();
  };

  const handleCancelSelectContact = () => {
    setState({ ...state, visibleModal: false, contact: null });
  };

  return (
    <Spin spinning={isFetching}>
      <div className="BulkUpdateForm">
        <Form form={form} scrollToFirstError layout="vertical" hideRequiredMark colon={false} onFinish={handleOnFinish}>
          {setting?.routePlanType === "PDP" && (
            <>
              <div className="FormHeader">
                <div className="left">{getTranslation(setting, "Orders", "Bulk Update Pickup")}</div>
                <div className="right">
                  <Link to="#" onClick={() => handleUseDefaultLocation("PICKUP")}>
                    {getTranslation(setting, "Orders", "Add from Contact List")}
                    <ReactSVG className="icon" src="/icons/default-location.svg" />
                  </Link>
                </div>
              </div>
              <div className="FormContent">
                <Row gutter={24}>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Pickup Name")}
                          title='Name of the customer you are picking up this order from. If you are picking up this order from a warehouse, use "Warehouse" as the pickup name.'
                        />
                      }
                      name="pickupName">
                      <Input size="large" placeholder={getTranslation(setting, "Orders", "Pickup Name")} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Pickup Phone Number")}
                          title="Phone number of the customer you are picking up this order from. Remember to include the country code and plus sign e.g. +17789123456 for US/Canada numbers or +6593456789 for Singapore numbers."
                        />
                      }
                      name="pickupPhone">
                      <PhoneInput
                        country={countryCodeInit}
                        enableSearch
                        disableSearchIcon
                        onChange={(value, country) => {
                          setCountryPickupPhone(country);
                          setCountryCookie(value, country, user);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Pickup From")}
                          title="The earliest time you need to pick up this order."
                        />
                      }
                      name="timeStart">
                      <TimePicker
                        size="large"
                        placeholder={getTranslation(setting, "Orders", "Pickup From")}
                        format={TIME_FORMAT_FRONTEND}
                        minuteStep={30}
                        style={{ width: "100%" }}
                        onChange={handleOnChangePickupFrom}
                        onSelect={handleOnChangePickupFrom}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Pickup To")}
                          title="The latest time you need to pick up this order."
                        />
                      }
                      name="timeEnd">
                      <TimePicker
                        size="large"
                        placeholder={getTranslation(setting, "Orders", "Pickup To")}
                        format={TIME_FORMAT_FRONTEND}
                        minuteStep={30}
                        style={{ width: "100%" }}
                        onChange={handleOnChangePickupTo}
                        onSelect={handleOnChangePickupTo}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Pickup Email")}
                          title="Email of the customer you are picking up this order from."
                        />
                      }
                      name="pickupEmail"
                      rules={[{ type: "email", message: "Invalid Email" }]}>
                      <Input size="large" placeholder={getTranslation(setting, "Orders", "Pickup Email")} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Pickup Address")}
                          title="Address of the customer you are picking up this order from."
                          required={false}
                        />
                      }
                      name="pickupAddress"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (value?.address && value?.latLng?.lat && value?.latLng?.lng) {
                              return Promise.resolve();
                            }

                            if (isEmpty(value?.address)) {
                              return Promise.resolve();
                            }

                            return Promise.reject(new Error("Invalid"));
                          },
                        },
                      ]}>
                      <AddressInput
                        size="large"
                        placeholder={getTranslation(setting, "Orders", "Pickup Address")}
                        onSelect={value => form.setFieldsValue({ pickupAddress: value })}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Pickup Duration")}
                          title="Pickup Duration"
                        />
                      }
                      name="pickupDuration">
                      <Input
                        size="large"
                        placeholder={getTranslation(setting, "Orders", "Pickup Duration")}
                        suffix="minutes"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </>
          )}
          <div className="FormHeader">
            <div className="left">{getTranslation(setting, "Orders", "Bulk Update Delivery")}</div>
            <div className="right">
              <Link to="#" onClick={() => handleUseDefaultLocation("DELIVERY")}>
                {getTranslation(setting, "Orders", "Add from Contact List")}
                <ReactSVG className="icon" src="/icons/default-location.svg" />
              </Link>
            </div>
          </div>
          <div className="FormContent">
            <Row gutter={24}>
              <Col span={6}>
                <Form.Item
                  label={
                    <InfoLabel
                      text={getTranslation(setting, "Orders", "Delivery Name")}
                      title="Name of the customer you are delivering this order to."
                    />
                  }
                  name="deliveryName">
                  <Input size="large" placeholder={getTranslation(setting, "Orders", "Delivery Name")} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={
                    <InfoLabel
                      text={getTranslation(setting, "Orders", "Delivery Phone Number")}
                      title="Phone number of the customer you are delivering this order to. If you've set up customer text notifications, the customer will receive text messages updating him on the status of this order. Remember to include the country code and plus sign e.g. +17789123456 for US/Canada numbers or +6593456789 for Singapore numbers."
                    />
                  }
                  name="deliveryPhone">
                  <PhoneInput
                    country={countryCodeInit}
                    enableSearch
                    disableSearchIcon
                    onChange={(value, country) => {
                      setCountryDeliveryPhone(country);
                      setCountryCookie(value, country, user);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={
                    <InfoLabel
                      text={getTranslation(setting, "Orders", "Delivery From")}
                      title="The earliest time you need to deliver this order."
                    />
                  }
                  name="deliveryTimeStart">
                  <TimePicker
                    size="large"
                    placeholder={getTranslation(setting, "Orders", "Delivery From")}
                    format={TIME_FORMAT_FRONTEND}
                    minuteStep={30}
                    style={{ width: "100%" }}
                    onChange={handleOnChangeDeliveryFrom}
                    onSelect={handleOnChangeDeliveryFrom}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={
                    <InfoLabel
                      text={getTranslation(setting, "Orders", "Delivery To")}
                      title="The latest time you need to deliver this order."
                    />
                  }
                  name="deliveryTimeEnd">
                  <TimePicker
                    size="large"
                    placeholder={getTranslation(setting, "Orders", "Delivery To")}
                    format={TIME_FORMAT_FRONTEND}
                    minuteStep={30}
                    style={{ width: "100%" }}
                    onChange={handleOnChangeDeliveryTo}
                    onSelect={handleOnChangeDeliveryTo}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={
                    <InfoLabel
                      text={getTranslation(setting, "Orders", "Delivery Email")}
                      title="Email of the customer you are picking up this order from. If you've set up customer email notifications, the customer will receive emails updating him on the status of this order."
                    />
                  }
                  name="deliveryEmail"
                  rules={[{ type: "email", message: "Invalid Email" }]}>
                  <Input size="large" placeholder={getTranslation(setting, "Orders", "Delivery Email")} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={
                    <InfoLabel
                      text={getTranslation(setting, "Orders", "Delivery Address")}
                      title="Address of the customer you are delivering this order to."
                      required={false}
                    />
                  }
                  name="deliveryAddress"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value?.address && value?.latLng?.lat && value?.latLng?.lng) {
                          return Promise.resolve();
                        }

                        if (isEmpty(value?.address)) {
                          return Promise.resolve();
                        }

                        return Promise.reject(new Error("Invalid"));
                      },
                    },
                  ]}>
                  <AddressInput
                    size="large"
                    placeholder={getTranslation(setting, "Orders", "Delivery Address")}
                    onSelect={value => form.setFieldsValue({ deliveryAddress: value })}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={
                    <InfoLabel
                      text={getTranslation(setting, "Orders", "Delivery Duration")}
                      title="Delivery Duration"
                    />
                  }
                  name="deliveryDuration">
                  <Input
                    size="large"
                    placeholder={getTranslation(setting, "Orders", "Delivery Duration")}
                    suffix="minutes"
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>
          <div className="FormHeader">
            <div className="left">{getTranslation(setting, "Orders", "Order Info")}</div>
          </div>
          <div className="FormContent">
            <Form.Item label={<InfoLabel text={getTranslation(setting, "Orders", "Labels")} title="Labels" />}>
              <LabelComponent labelIds={labelIds} setLabelIds={setLabelIds} />
            </Form.Item>
            <Row gutter={24}>
              <Col span={6}>
                <Form.Item
                  label={
                    <InfoLabel
                      text={getTranslation(setting, "Orders", "Types")}
                      title="For advanced users only. Use this to match specific orders with drivers that are equipped to do those orders e.g. a VIP driver can pickup and deliver VIP orders."
                    />
                  }>
                  <TagInput tags={types} onChange={values => setTypes(values)} />
                </Form.Item>
              </Col>
              {setting?.routePlanType === "PDP" && (
                <Col span={9}>
                  <Form.Item
                    label={
                      <InfoLabel
                        text={getTranslation(setting, "Orders", "Pickup Notes")}
                        title='Any information that can help the driver successfully make his delivery e.g. "Leave the package on the porch".'
                      />
                    }
                    name="pickupNotes">
                    <Input.TextArea rows={4} placeholder={getTranslation(setting, "Orders", "Pickup Notes")} />
                  </Form.Item>
                </Col>
              )}
              <Col span={9}>
                <Form.Item
                  label={
                    <InfoLabel
                      text={getTranslation(setting, "Orders", "Delivery Notes")}
                      title='Any information that can help the driver successfully make his delivery e.g. "Leave the package on the porch".'
                    />
                  }
                  name="deliveryNotes">
                  <Input.TextArea rows={4} placeholder={getTranslation(setting, "Orders", "Delivery Notes")} />
                </Form.Item>
              </Col>
            </Row>
          </div>
          <div className="FormFooter">
            <div className="Left"></div>
            <div className="Right">
              <Button key="cancel" onClick={handleCancel}>
                {getTranslation(setting, "Common", "Cancel")}
              </Button>
              <Button htmlType="submit" type="primary">
                {getTranslation(setting, "Common", "Update")}
                <ReactSVG className="icon" src="/icons/submit.svg" />
              </Button>
            </div>
          </div>
        </Form>
      </div>
      <Modal open={state.visibleModal} onCancel={handleCancelSelectContact} footer={[]} width={792}>
        <DefaultContactLocation
          state={state}
          setState={setState}
          handleCancel={handleCancelSelectContact}
          handleSubmit={handleSubmitSelectContact}
        />
      </Modal>
    </Spin>
  );
};

export default BulkUpdateForm;
